import {
  DeleteOutlined,
  KeyOutlined,
  PlusOutlined,
  StarFilled,
} from '@ant-design/icons';
import {
  Button,
  Card,
  Form,
  Input,
  Modal,
  Popconfirm,
  Result,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useEffect, useState } from 'react';
import { AppLayout } from '../components/AppLayout';
import { PageHead } from '../components/PageHead';
import { SetPasswordModal } from '../components/SetPasswordModal';
import { api, type TeamMemberRow, type TeamRole } from '../lib/api';
import { useIsSuperAdmin } from '../lib/me';

const { Title, Text } = Typography;

const ROLE_LABEL: Record<TeamRole, string> = {
  super_admin: 'Super admin',
  admin: 'Admin',
};

export function AdminUsersScene() {
  const isSuper = useIsSuperAdmin();
  const [members, setMembers] = useState<TeamMemberRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [pwdTarget, setPwdTarget] = useState<TeamMemberRow | null>(null);
  const [form] = Form.useForm<{ email: string; name: string; role: TeamRole }>();

  const load = async () => {
    setLoading(true);
    try {
      setMembers(await api<TeamMemberRow[]>('/api/team-members'));
    } catch (err) {
      message.error((err as { error?: string }).error ?? 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  if (!isSuper) {
    return (
      <AppLayout>
        <PageHead title="Admin users" />
        <Result
          status="403"
          title="Super-admin only"
          subTitle="Ask your AOTV super-admin to grant you access if you need to manage team members."
        />
      </AppLayout>
    );
  }

  const onAdd = async (values: { email: string; name: string; role: TeamRole }) => {
    setAdding(true);
    try {
      await api('/api/team-members', {
        method: 'POST',
        body: JSON.stringify(values),
      });
      form.resetFields();
      setAddOpen(false);
      await load();
    } catch (err) {
      console.error('Add admin failed', err);
      const e = err as { error?: string; status?: number };
      message.error(e.error ?? `Failed to add (status ${e.status ?? '?'})`);
    } finally {
      setAdding(false);
    }
  };

  const remove = async (row: TeamMemberRow) => {
    try {
      await api(`/api/team-members/${row.id}`, { method: 'DELETE' });
      await load();
    } catch (err) {
      message.error((err as { error?: string }).error ?? 'Failed to delete');
    }
  };

  const setRole = async (row: TeamMemberRow, role: TeamRole) => {
    try {
      await api(`/api/team-members/${row.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ role }),
      });
      await load();
    } catch (err) {
      message.error((err as { error?: string }).error ?? 'Failed to update');
    }
  };

  const columns: ColumnsType<TeamMemberRow> = [
    {
      title: 'Name',
      dataIndex: 'name',
      render: (name: string, row) => (
        <div>
          <Text strong>
            {row.role === 'super_admin' && (
              <StarFilled style={{ color: '#faad14', marginRight: 6 }} />
            )}
            {name}
          </Text>
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {row.email}
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: 'Role',
      dataIndex: 'role',
      width: 220,
      render: (role: TeamRole, row) => (
        <Select
          size="small"
          value={role}
          options={[
            { value: 'super_admin', label: ROLE_LABEL.super_admin },
            { value: 'admin', label: ROLE_LABEL.admin },
          ]}
          onChange={(v) => setRole(row, v)}
        />
      ),
    },
    {
      title: 'Password',
      dataIndex: 'has_password',
      width: 140,
      render: (h: boolean) => (h ? <Tag color="green">Set</Tag> : <Tag>None</Tag>),
    },
    {
      title: '',
      key: 'actions',
      width: 220,
      render: (_t, row) => (
        <Space>
          <Button
            size="small"
            icon={<KeyOutlined />}
            onClick={() => setPwdTarget(row)}
          >
            {row.has_password ? 'Reset password' : 'Set password'}
          </Button>
          <Popconfirm
            title="Remove this team member?"
            description={
              row.role === 'super_admin'
                ? 'Last super admin can\'t be removed.'
                : 'Their event assignments will be cleared.'
            }
            okText="Remove"
            okButtonProps={{ danger: true }}
            onConfirm={() => remove(row)}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <AppLayout>
      <PageHead title="Admin users" />
      <Space
        align="center"
        style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}
      >
        <Title level={2} style={{ margin: 0 }}>
          Admin users
        </Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>
          Add admin
        </Button>
      </Space>
      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        Super admins see and re-assign every event. Regular admins only see
        events they own (plus legacy un-assigned events).
      </Text>

      <Card bodyStyle={{ padding: 0 }}>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={members}
          loading={loading}
          pagination={false}
        />
      </Card>

      <Modal
        title="Add admin"
        open={addOpen}
        onCancel={() => setAddOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={adding}
        okText="Add"
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onAdd}
          initialValues={{ role: 'admin' }}
        >
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Please enter a name' }]}
          >
            <Input autoFocus />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please enter an email' },
              { type: 'email', message: 'Not a valid email' },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="role" label="Role">
            <Select
              options={[
                { value: 'admin', label: ROLE_LABEL.admin },
                { value: 'super_admin', label: ROLE_LABEL.super_admin },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>

      <SetPasswordModal
        open={pwdTarget !== null}
        // Repurposing the speaker password modal — same generate-once UX,
        // but pointing at the team-members endpoint.
        speakerId={pwdTarget?.id ?? null}
        speakerName={pwdTarget?.name ?? ''}
        onClose={() => setPwdTarget(null)}
        onDone={load}
        passwordEndpoint={pwdTarget ? `/api/team-members/${pwdTarget.id}/password` : null}
      />
    </AppLayout>
  );
}
